import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../../supabaseClient";
import ShieldImg from "../../Assets/Icons/shield.png";
import LoaderBox from "../HomePage/LoaderBox";
import AdminResultsShare from "./AdminResultsShare";

const AdminEditResults = ({ turnir }) => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showResults, setShowResults] = useState(true);
  const [activeFilter, setActiveFilter] = useState("Sve");
  const [expandedMatchId, setExpandedMatchId] = useState(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [activeMatchForShare, setActiveMatchForShare] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventModalOpen, setEventModalOpen] = useState(false);

  const [editBuffer, setEditBuffer] = useState({}); // privremene izmjene po utakmici
  const tournamentId = turnir?.id;

  const filterOptions = [
    "Sve",
    "Grupa A",
    "Grupa B",
    "Grupa C",
    "Grupa D",
    "Grupa E",
    "Grupa F",
    "Grupa G",
    "Grupa H",
    "Završnica",
  ];

  const recalcScore = (events) => {
    let a = 0;
    let b = 0;

    events.forEach((e) => {
      if (e.type === "goal" || e.type === "penalty") {
        e.team === "A" ? a++ : b++;
      }

      if (e.type === "og") {
        e.team === "A" ? b++ : a++;
      }
    });

    return { a, b };
  };

  const fetchSettings = useCallback(async () => {
    if (!tournamentId) return;

    const { data, error } = await supabase
      .from("admin_turniri")
      .select("prikazi_rezultate")
      .eq("id", tournamentId)
      .maybeSingle();

    if (error) {
      console.error("Greška pri dohvaćanju postavki:", error.message);
      return;
    }

    if (data) {
      setShowResults(data.prikazi_rezultate);
    }
  }, [tournamentId]);

  const fetchResults = useCallback(async () => {
    if (!tournamentId) {
      return;
    }
    setLoading(true);

    const { data: regData } = await supabase
      .from("turniri_utakmice")
      .select(`*, team_a:team_a_id (*), team_b:team_b_id (*)`)
      .eq("tournament_id", tournamentId)
      .eq("is_completed", true);

    const { data: vetData } = await supabase
      .from("turniri_veterani")
      .select(`*, team_a:team_a_id (*), team_b:team_b_id (*)`)
      .eq("tournament_id", tournamentId)
      .eq("is_completed", true);

    const formattedReg = (regData || []).map((m) => ({
      ...m,
      isVeteran: false,
    }));
    const formattedVet = (vetData || []).map((m) => ({
      ...m,
      isVeteran: true,
    }));

    const data = [...formattedReg, ...formattedVet];

    if (data) {
      data.sort((a, b) => {
        if (a.group_name === "Završnica" && b.group_name !== "Završnica")
          return 1;
        if (a.group_name !== "Završnica" && b.group_name === "Završnica")
          return -1;

        if (a.group_name !== "Završnica" && b.group_name !== "Završnica") {
          if (a.group_name !== b.group_name)
            return a.group_name.localeCompare(b.group_name);
        }

        return (a.bracket_slot_id || 0) - (b.bracket_slot_id || 0);
      });

      setResults(data);
    }

    setLoading(false);
  }, [tournamentId]);

  useEffect(() => {
    fetchResults();
    fetchSettings();
  }, [fetchResults, fetchSettings]);

  // ---------------- FILTER ----------------
  const filteredResults = results.filter((match) => {
    if (activeFilter === "Sve") return true;

    if (activeFilter === "Završnica") {
      return match.group_name === "Završnica" || match.bracket_slot_id != null;
    }

    const filterValue = activeFilter.includes(" ")
      ? activeFilter.split(" ")[1]
      : activeFilter;

    return match.group_name === filterValue;
  });

  // ---------------- EXPAND MATCH ----------------
  const toggleExpand = (match) => {
    if (expandedMatchId === match.id) {
      setExpandedMatchId(null);
      return;
    }

    setExpandedMatchId(match.id);

    setEditBuffer((prev) => ({
      ...prev,
      [match.id]: {
        scorea: match.scorea ?? 0,
        scoreb: match.scoreb ?? 0,
        events: match.events || [],
      },
    }));
  };

  // ---------------- UPDATE SCORE ----------------
  const updateScore = (matchId, field, value) => {
    setEditBuffer((prev) => ({
      ...prev,
      [matchId]: {
        ...prev[matchId],
        [field]: value === "" ? "" : Math.max(0, Number(value)),
      },
    }));
  };

  // ---------------- ADD EVENT ----------------
  const addEvent = (matchId, team) => {
    const matchObj = results.find((m) => m.id === matchId);

    // Osiguraj da buffer za ovu utakmicu postoji prije dodavanja događaja
    setEditBuffer((prev) => {
      const currentBuffer = prev[matchId] || {
        scorea: matchObj?.scorea ?? 0,
        scoreb: matchObj?.scoreb ?? 0,
        events: matchObj?.events || [],
      };

      const newEvent = {
        id: Date.now(),
        team,
        player: "",
        time: "",
        type: "goal",
        matchId: matchId,
      };

      setEditingEvent(newEvent);
      setEventModalOpen(true);

      return {
        ...prev,
        [matchId]: {
          ...currentBuffer,
          events: [...currentBuffer.events, newEvent],
        },
      };
    });
  };
  const saveEvent = (updatedEvent) => {
    const matchId = updatedEvent.matchId;

    setEditBuffer((prev) => {
      const currentEvents = prev[matchId].events || [];
      const isExisting = currentEvents.find((e) => e.id === updatedEvent.id);

      let newEvents;
      if (isExisting) {
        newEvents = currentEvents.map((ev) =>
          ev.id === updatedEvent.id ? updatedEvent : ev,
        );
      } else {
        newEvents = [...currentEvents, updatedEvent];
      }

      const { a, b } = recalcScore(newEvents);

      return {
        ...prev,
        [matchId]: {
          ...prev[matchId],
          scorea: a,
          scoreb: b,
          events: newEvents,
        },
      };
    });

    setEventModalOpen(false);
    setEditingEvent(null);
  };

  // ---------------- OPEN EVENT EDIT ----------------
  const openEventEdit = (event, matchId) => {
    setEditingEvent({ ...event, matchId });
    setEventModalOpen(true);
  };

  // ---------------- DELETE EVENT ----------------
  const deleteEvent = (matchId, eventId) => {
    if (!window.confirm("Jeste li sigurni da želite obrisati ovaj događaj?")) {
      return;
    }

    setEditBuffer((prev) => {
      const updatedEvents = prev[matchId].events.filter(
        (e) => e.id !== eventId,
      );
      const { a, b } = recalcScore(updatedEvents);
      return {
        ...prev,
        [matchId]: {
          ...prev[matchId],
          scorea: a,
          scoreb: b,
          events: updatedEvents,
        },
      };
    });
    setEventModalOpen(false);
    setEditingEvent(null);
  };

  // ---------------- SAVE MATCH ----------------
  const saveMatch = async (matchId) => {
    const buffer = editBuffer[matchId];
    const matchObj = results.find((m) => m.id === matchId);
    const targetTable = matchObj?.isVeteran
      ? "turniri_veterani"
      : "turniri_utakmice";

    const { a, b } = recalcScore(buffer.events);

    const { error } = await supabase
      .from(targetTable)
      .update({
        scorea: a,
        scoreb: b,
        events: buffer.events,
      })
      .eq("id", matchId);

    if (!error) {
      fetchResults();
      setExpandedMatchId(null);
    } else {
      alert("Greška pri spremanju!");
    }
  };

  if (loading) return <LoaderBox text="Učitavanje rezultata..." />;

  return (
    <div className="admin-edit-results">
      <div className="visibility-control-bar">
        <span className="control-label">Prikaži rezultate na webu</span>
        <label className="ios-switch">
          <input
            type="checkbox"
            checked={showResults}
            onChange={async (event) => {
              const value = event.target.checked;
              setShowResults(value);

              await supabase
                .from("admin_turniri")
                .update({ prikazi_rezultate: value })
                .eq("id", tournamentId);
            }}
          />
          <span className="slider"></span>
        </label>
      </div>

      <div className="filter-nav-scroll">
        {filterOptions.map((opt) => (
          <button
            key={opt}
            className={`filter-chip ${activeFilter === opt ? "active" : ""}`}
            onClick={() => setActiveFilter(opt)}
          >
            {opt}
          </button>
        ))}
      </div>

      <div className="results-container">
        {filteredResults.length > 0 ? (
          filteredResults.map((match) => {
            const isOpen = expandedMatchId === match.id;
            const buffer = editBuffer[match.id] || {
              scorea: match.scorea ?? 0,
              scoreb: match.scoreb ?? 0,
              events: match.events || [],
            };

            return (
              <div key={match.id} className="result-card admin-edit-card">
                <div className="result-header">
                  <button
                    className="share-insta-btn"
                    onClick={() => {
                      setActiveMatchForShare(match);
                      setShareModalOpen(true);
                    }}
                    title="Izvezi za Instagram"
                  >
                    <i className="fi fi-brands-instagram"></i>
                  </button>
                  <div
                    className="result-group-tag"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    {match.isVeteran && <span>VETERANI - </span>}
                    <span>
                      {match.group_name === "Završnica"
                        ? (() => {
                            const id = match.bracket_slot_id;
                            if (id >= 1 && id <= 8) return "1/8 FINALE";
                            if (id >= 9 && id <= 12) return "1/4 FINALE";
                            if (id === 13 || id === 14) return "POLUFINALE";
                            if (id === 15) return "FINALE";
                            if (id === 16) return "3. MJESTO";
                            return "ZAVRŠNICA";
                          })()
                        : `GRUPA ${match.group_name}`}
                    </span>
                  </div>

                  <button
                    className="edit-btn"
                    onClick={() => toggleExpand(match)}
                  >
                    <i className="fi fi-rs-pencil"></i>
                  </button>
                </div>

                <div className="result-main">
                  <div className="res-team">
                    <img src={match.team_a?.logo_url || ShieldImg} alt="" />
                    <span>{match.team_a?.name}</span>
                  </div>

                  <div className="res-score">
                    {isOpen ? (
                      <>
                        <input
                          type="number"
                          value={buffer?.scorea ?? ""}
                          onChange={(e) =>
                            updateScore(match.id, "scorea", e.target.value)
                          }
                        />
                        <span>:</span>
                        <input
                          type="number"
                          value={buffer?.scoreb ?? ""}
                          onChange={(e) =>
                            updateScore(match.id, "scoreb", e.target.value)
                          }
                        />
                      </>
                    ) : (
                      <>
                        <span className="score-num">{match.scorea}</span>
                        <span>:</span>
                        <span className="score-num">{match.scoreb}</span>
                      </>
                    )}
                  </div>

                  <div className="res-team">
                    <img src={match.team_b?.logo_url || ShieldImg} alt="" />
                    <span>{match.team_b?.name}</span>
                  </div>
                </div>

                {isOpen && buffer && (
                  <div className="expanded-panel">
                    <div className="add-event-row">
                      <button onClick={() => addEvent(match.id, "A")}>
                        + Domaći događaj
                      </button>
                      <button onClick={() => addEvent(match.id, "B")}>
                        + Gosti događaj
                      </button>
                    </div>

                    <div className="timeline-admin">
                      {buffer.events
                        .slice()
                        .sort((a, b) => Number(a.time) - Number(b.time))
                        .map((ev) => (
                          <div
                            key={ev.id}
                            className={`event-row ${
                              ev.team === "A" ? "left" : "right"
                            }`}
                          >
                            <div className="event-box">
                              <button
                                className="edit-event-btn"
                                onClick={() => openEventEdit(ev, match.id)}
                              >
                                ✎
                              </button>

                              <span className="time">{ev.time}'</span>

                              <EventIcon type={ev.type} />

                              <span className="player">{ev.player}</span>

                              <button
                                className="delete-event-btn"
                                onClick={() => deleteEvent(match.id, ev.id)}
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        ))}
                    </div>

                    <div className="edit-actions">
                      <button
                        className="cancel-btn"
                        onClick={() => {
                          setExpandedMatchId(null);
                          setEditBuffer((prev) => {
                            const copy = { ...prev };
                            delete copy[match.id];
                            return copy;
                          });
                        }}
                      >
                        Odustani
                      </button>

                      <button
                        className="save-btn"
                        onClick={() => saveMatch(match.id)}
                      >
                        Spremi promjene
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <p className="no-results">Nema rezultata za odabrani filter</p>
        )}
      </div>
      {shareModalOpen && activeMatchForShare && (
        <AdminResultsShare
          match={activeMatchForShare}
          turnir={turnir}
          turnirNaziv={turnir?.naziv}
          onClose={() => {
            setShareModalOpen(false);
            setActiveMatchForShare(null);
          }}
        />
      )}
      {eventModalOpen && editingEvent && (
        <div className="modal-overlay">
          <div className="event-modal">
            <h3>Uredi događaj</h3>

            <input
              type="number"
              value={editingEvent.time}
              onChange={(e) =>
                setEditingEvent({
                  ...editingEvent,
                  time: e.target.value,
                })
              }
              placeholder="Minuta"
            />

            <select
              value={editingEvent.player}
              onChange={(e) =>
                setEditingEvent({
                  ...editingEvent,
                  player: e.target.value,
                })
              }
            >
              <option value="">Odaberi igrača</option>

              {(() => {
                const matchId =
                  editingEvent.matchId ||
                  Object.keys(editBuffer).find((id) =>
                    editBuffer[id]?.events?.some(
                      (e) => e.id === editingEvent.id,
                    ),
                  );
                const currentMatch = results.find((m) => m.id === matchId);
                const targetEvents =
                  editBuffer[matchId]?.events || currentMatch?.events || [];
                const currentEvent = targetEvents.find(
                  (e) => e.id === editingEvent.id,
                );

                const roster =
                  (currentEvent?.team || editingEvent.team) === "A"
                    ? currentMatch?.team_a?.players
                    : currentMatch?.team_b?.players;

                return (roster || []).map((p, i) => (
                  <option key={i} value={p.name}>
                    {p.no}. {p.name}
                  </option>
                ));
              })()}
            </select>

            <select
              value={editingEvent.type}
              onChange={(e) =>
                setEditingEvent({
                  ...editingEvent,
                  type: e.target.value,
                })
              }
              className="event-type-select"
            >
              <option value="goal">GOL</option>
              <option value="penalty">PENAL</option>
              <option value="penalty-miss">PROMAŠENI PENAL</option>
              <option value="yellow">2 MIN (KARTON)</option>
              <option value="og">AUTOGOL</option>
            </select>

            <div className="modal-btns-row">
              <button
                className="add-btn"
                onClick={() => saveEvent(editingEvent)}
              >
                SPREMI
              </button>

              <button
                className="delete-btn"
                onClick={() => {
                  const matchId = Object.keys(editBuffer).find((id) =>
                    editBuffer[id].events.some((e) => e.id === editingEvent.id),
                  );

                  deleteEvent(matchId, editingEvent.id);
                  setEventModalOpen(false);
                  setEditingEvent(null);
                }}
              >
                OBRIŠI
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const EventIcon = ({ type }) => {
  switch (type) {
    case "goal":
      return (
        <div className="goal-icon" title="Gol">
          <i className="fi fi-br-football"></i>
        </div>
      );
    case "penalty":
      return (
        <div className="penalty-icon" title="Penal">
          <i className="fi fi-br-football"></i>
        </div>
      );
    case "penalty-miss":
      return (
        <div className="penalty-icon miss" title="Promašaj">
          <i className="fi fi-rs-cross-small"></i>
        </div>
      );
    case "yellow":
      return (
        <div className="two-min-out-icon" title="2 min">
          <p>2</p>
          <span>min</span>
        </div>
      );
    case "og":
      return (
        <div className="c-icon c-og" title="Autogol">
          AG
        </div>
      );
    default:
      return <div className="c-icon c-default"></div>;
  }
};

export default AdminEditResults;
